import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  Map as MlMap, NavigationControl, Popup, ScaleControl, setWorkerUrl,
  type GeoJSONSource,
} from 'maplibre-gl'
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import 'maplibre-gl/dist/maplibre-gl.css'
import { buildRasterStyle, rasterBasemap } from '../map/basemaps'
import { useConfig } from '../lib/useConfig'
import type { PhotorealHandle } from '../map/photoreal'
import { iconFor, MARKER_COLOR, registerVehicleIcons, truck, type MarkerState } from '../map/layers/markers'
import { BUS_ICON_PIXEL_RATIO, BUS_MODELS, busIconImageData, type BusModel } from '../busOps/busIcons'
import { EIM_STATUS_STYLE } from '../eim/statusStyle'
import type { GlobalFleet, GlobalState, GlobalVehicle } from './useGlobalApi'

let workerSet = false
function ensureWorker(): void {
  if (workerSet) return
  setWorkerUrl(workerUrl)
  workerSet = true
}

const FLEET_LABEL: Record<GlobalFleet, string> = {
  ultratech: 'Ultratech', mbmt: 'MBMT', eim: 'Ports', star: 'STAR Cement', nagpur: 'Nagpur',
  wondercement: 'Wonder Cement', tupl: 'TUPL',
}
const STATE_LABEL: Record<GlobalState, string> = {
  moving: 'Moving', idle: 'Idle', charging: 'Charging', stale: 'Offline', down: 'Breakdown',
}

/**
 * Bus glyphs need a paired EDGE shade (the drawing function darkens the windscreen
 * border with it); MARKER_COLOR -- the truck palette every other glyph on this map draws
 * from -- only carries one hex per state, because the truck silhouette derives its own
 * outline from a fixed white rather than a second colour. Reusing MbmtLiveMap's own
 * tested fill/edge pairs here keeps the bus glyph's contrast exactly as tuned on MBMT's
 * dedicated live map, rather than inventing new edge shades for hues that were never
 * paired with one. No 'charging' entry: MBMT telemetry carries no charging signal (see
 * useGlobalApi.ts's mbmtToGlobal), so that state can never reach a bus icon.
 */
const BUS_FILL_EDGE: Partial<Record<GlobalState, { fill: string; edge: string }>> = {
  down: { fill: '#F43F5E', edge: '#9F1239' },
  moving: { fill: '#10B981', edge: '#047857' },
  idle: { fill: '#F59E0B', edge: '#B45309' },
  stale: { fill: '#94A3B8', edge: '#64748B' },
}

function busIconId(state: GlobalState, model: BusModel): string {
  return `global-bus-${state}-${model}`
}

/** Ports (EIM) charging trucks draw in their own console's teal (statusStyle.ts), not
 *  Ultratech's blue MARKER_COLOR.charging -- the two consoles deliberately diverged on
 *  this one colour (Ultratech's charging blue was never in question; EIM's moved off
 *  blue specifically so it wouldn't collide with EIM's own idle-alert blue). Sharing the
 *  truck silhouette between the two fleets is still right; sharing this one colour is not. */
const EIM_CHARGING_ICON = 'global-truck-eim-charging'

/** Idempotent, same contract as registerVehicleIcons itself: style reloads re-run this
 *  and addImage throws on a duplicate id. */
function registerGlobalIcons(map: MlMap): void {
  registerVehicleIcons(map)
  const ratio = Math.min(3, Math.max(1, Math.round(window.devicePixelRatio || 1)))
  if (!map.hasImage(EIM_CHARGING_ICON)) {
    map.addImage(EIM_CHARGING_ICON, truck(EIM_STATUS_STYLE.charging.hex, false, ratio), { pixelRatio: ratio })
  }
  for (const [state, sty] of Object.entries(BUS_FILL_EDGE) as [GlobalState, { fill: string; edge: string }][]) {
    for (const model of BUS_MODELS) {
      const id = busIconId(state, model)
      if (map.hasImage(id)) continue
      map.addImage(id, busIconImageData(sty.fill, sty.edge, model), { pixelRatio: BUS_ICON_PIXEL_RATIO })
    }
  }
}

function iconIdFor(v: GlobalVehicle): string {
  if (v.fleet === 'mbmt' || v.fleet === 'nagpur') {
    // A bus glyph never carries 'charging' (BUS_FILL_EDGE has no entry for it) -- neither
    // MBMT's nor Nagpur's status derivation in useGlobalApi.ts ever emits that state, so
    // this can't miss. Nagpur's busModel is always null (BusBoardRow carries no
    // vehicletype) -- '12M_NAC' is the same fallback busModel(undefined) itself resolves
    // to, matching what BusRouteMap.tsx's own live map already draws for these buses.
    return busIconId(v.state, v.busModel ?? '12M_NAC')
  }
  if (v.fleet === 'eim' && v.state === 'charging') return EIM_CHARGING_ICON
  // Ultratech and EIM are both, literally, trucks -- sharing the exact glyph the corridor
  // map already uses (recoloured by the same MARKER_COLOR state) reads as correct rather
  // than inventing a visually-arbitrary second truck silhouette for EIM specifically.
  // Never laden: load state is an Ultratech-corridor concept (which leg of a fixed route)
  // that has no equivalent for a port yard hauler or this map's zoomed-out vantage.
  return iconFor(v.state as MarkerState, false)
}

function toGeoJSON(vehicles: GlobalVehicle[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: vehicles.map((v) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [v.lon, v.lat] },
      properties: {
        id: v.id,
        fleet: v.fleet,
        fleetLabel: FLEET_LABEL[v.fleet],
        state: v.state,
        stateLabel: STATE_LABEL[v.state],
        icon: iconIdFor(v),
        heading: v.heading,
        speed: v.speedKph,
        soc: v.socPct,
        sub: v.sub,
      },
    })),
  }
}

/** Unlike EimPortsMap's single-port fit (which excludes a genuine outlier so one truck
 *  can't zoom a whole port out to a speck), three fleets spread across three-plus states
 *  is a legitimately national footprint -- a plain bounding box is the honest fit here,
 *  not a radius-filtered one. */
function fitToVehicles(map: MlMap, vehicles: GlobalVehicle[]): void {
  if (vehicles.length === 0) return
  if (vehicles.length === 1) {
    const v = vehicles[0]!
    map.easeTo({ center: [v.lon, v.lat], zoom: 12, duration: 600 })
    return
  }
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity
  for (const v of vehicles) {
    minLng = Math.min(minLng, v.lon); maxLng = Math.max(maxLng, v.lon)
    minLat = Math.min(minLat, v.lat); maxLat = Math.max(maxLat, v.lat)
  }
  map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 72, duration: 600, maxZoom: 12 })
}

function popupHtml(p: Record<string, unknown>): string {
  const soc = p.soc != null ? `${Math.round(Number(p.soc))}%` : null
  const speed = p.speed != null ? `${Math.round(Number(p.speed))} km/h` : null
  return (
    `<div style="min-width:170px;font-family:Inter,sans-serif">`
    + `<div style="display:flex;align-items:center;gap:6px">`
    + `<span style="font:700 12px ui-monospace,monospace;color:#0F172A">${p.id}</span>`
    + `<span style="font:600 9px sans-serif;letter-spacing:.08em;text-transform:uppercase;color:#94A3B8">${p.fleetLabel}</span>`
    + `</div>`
    + `<div style="font:600 13px Inter,sans-serif;color:#0F172A;margin-top:3px">${p.stateLabel}</div>`
    + `<div style="font:11px Inter,sans-serif;color:#64748B;margin-top:2px">`
    + [p.sub, speed, soc ? `${soc} SOC` : null].filter(Boolean).join(' · ') || '—'
    + `</div></div>`
  )
}

const INDIA_CENTER: [number, number] = [79.5, 22.5]

export function GlobalMap({ vehicles, filter }: {
  vehicles: GlobalVehicle[]
  filter: GlobalFleet | 'all'
}): JSX.Element {
  const container = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MlMap | null>(null)
  const styleReady = useRef(false)
  const vehiclesRef = useRef(vehicles)
  vehiclesRef.current = vehicles
  const filterRef = useRef(filter)
  filterRef.current = filter
  const fittedForRef = useRef<string | null>(null)

  // Photoreal 3D, opt-in -- same pattern as EimPortsMap.tsx/StarMap.tsx/BusRouteMap.tsx.
  // Purely additive; the default OSM raster underneath is unchanged.
  const [mapLoaded, setMapLoaded] = useState(false)
  const [photorealOn, setPhotorealOn] = useState(false)
  const { photoreal: photorealAvailableCfg, maps_key: mapsKeyCfg } = useConfig()
  const photorealAvailable = Boolean(photorealAvailableCfg)
  const mapsKey = mapsKeyCfg || ''
  const [attribution, setAttribution] = useState<string[]>([])
  const photorealRef = useRef<PhotorealHandle | null>(null)

  useEffect(() => {
    if (!container.current) return
    ensureWorker()
    const basemap = rasterBasemap('osm')
    if (!basemap) return
    const map = new MlMap({
      container: container.current,
      style: buildRasterStyle(basemap),
      center: INDIA_CENTER,
      zoom: 4.4,
      attributionControl: { compact: true },
    })
    mapRef.current = map
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right')
    map.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left')

    const hoverPopup = new Popup({ closeButton: false, closeOnClick: false, offset: 18 })
    const clickPopup = new Popup({ closeButton: true, closeOnClick: false, offset: 22 })

    // This container's height comes from a flex-1 ancestor, not a fixed pixel class (unlike
    // EimPortsMap's panels) -- maplibre measures the container once at construction, and a
    // flex layout that hasn't finished resolving at that instant leaves the map permanently
    // sized to whatever it read (often 0), same reasoning MapCanvas.tsx and MbmtLiveMap.tsx
    // already apply their own ResizeObserver for.
    map.once('load', () => map.resize())
    const ro = new ResizeObserver(() => map.resize())
    ro.observe(container.current)

    map.on('load', () => {
      registerGlobalIcons(map)
      map.addSource('global-vehicles', {
        type: 'geojson',
        data: toGeoJSON([]),
        cluster: true,
        clusterRadius: 46,
        clusterMaxZoom: 9,
      })
      map.addLayer({
        id: 'global-cluster-halo',
        type: 'circle',
        source: 'global-vehicles',
        filter: ['has', 'point_count'],
        paint: {
          'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 30, 26],
          'circle-color': '#0F172A',
          'circle-opacity': 0.12,
        },
      })
      map.addLayer({
        id: 'global-clusters',
        type: 'circle',
        source: 'global-vehicles',
        filter: ['has', 'point_count'],
        paint: {
          'circle-radius': ['step', ['get', 'point_count'], 12, 10, 15, 30, 20],
          'circle-color': '#0F172A',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#FFFFFF',
        },
      })
      map.addLayer({
        id: 'global-cluster-count',
        type: 'symbol',
        source: 'global-vehicles',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['Noto Sans Bold'],
          'text-size': 11,
          'text-allow-overlap': true,
        },
        paint: { 'text-color': '#FFFFFF' },
      })
      map.addLayer({
        id: 'global-vehicles',
        type: 'symbol',
        source: 'global-vehicles',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-rotate': ['get', 'heading'],
          'icon-rotation-alignment': 'map',
          'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.22, 8, 0.32, 12, 0.5, 16, 0.75],
        },
      })

      map.on('mouseenter', 'global-vehicles', (e) => {
        map.getCanvas().style.cursor = 'pointer'
        const f = e.features?.[0]
        if (!f || f.geometry.type !== 'Point') return
        hoverPopup.setLngLat(f.geometry.coordinates as [number, number])
          .setHTML(popupHtml(f.properties ?? {})).addTo(map)
      })
      map.on('mouseleave', 'global-vehicles', () => {
        map.getCanvas().style.cursor = ''
        hoverPopup.remove()
      })
      map.on('click', 'global-vehicles', (e) => {
        const f = e.features?.[0]
        if (!f || f.geometry.type !== 'Point') return
        hoverPopup.remove()
        clickPopup.setLngLat(f.geometry.coordinates as [number, number])
          .setHTML(popupHtml(f.properties ?? {})).addTo(map)
      })
      map.on('click', (e) => {
        if (!map.queryRenderedFeatures(e.point, { layers: ['global-vehicles'] }).length) clickPopup.remove()
      })
      map.on('mouseenter', 'global-clusters', () => { map.getCanvas().style.cursor = 'pointer' })
      map.on('mouseleave', 'global-clusters', () => { map.getCanvas().style.cursor = '' })
      map.on('click', 'global-clusters', (e) => {
        const f = e.features?.[0]
        const clusterId = f?.properties?.['cluster_id']
        const src = map.getSource('global-vehicles')
        if (!f || typeof clusterId !== 'number' || !(src instanceof Object) || f.geometry.type !== 'Point') return
        const gj = src as GeoJSONSource
        void gj.getClusterExpansionZoom(clusterId).then((zoom) => {
          map.easeTo({ center: (f.geometry as GeoJSON.Point).coordinates as [number, number], zoom: zoom ?? map.getZoom() + 2 })
        })
      })

      styleReady.current = true
      const src = map.getSource('global-vehicles') as GeoJSONSource
      src.setData(toGeoJSON(vehiclesRef.current))
      // NOT fit-and-mark-done here unconditionally: the style usually finishes loading
      // before the first MBMT/EIM poll (or even the Ultratech store) has any data, so
      // vehiclesRef.current is often still empty at this exact instant. Marking
      // fittedForRef done regardless would permanently skip the real fit once data
      // actually arrives -- leave it null and let the effect below (which re-checks on
      // every `vehicles` update, not just once) perform the first real fit.
      if (vehiclesRef.current.length > 0) {
        fitToVehicles(map, vehiclesRef.current)
        fittedForRef.current = filterRef.current
      }
      setMapLoaded(true)
    })

    return () => {
      ro.disconnect()
      photorealRef.current?.destroy()
      photorealRef.current = null
      map.remove()
      mapRef.current = null
      styleReady.current = false
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapLoaded) return
    if (photorealOn && !photorealRef.current && mapsKey) {
      let cancelled = false
      void import('../map/photoreal').then(({ addPhotoreal }) => {
        if (cancelled || photorealRef.current || !mapRef.current) return
        photorealRef.current = addPhotoreal(mapRef.current, mapsKey, {
          onLoad: () => {},
          onAttribution: (credits) => setAttribution(credits),
          onError: () => {},
        })
      })
      return () => { cancelled = true }
    }
    if (!photorealOn && photorealRef.current) {
      photorealRef.current.destroy()
      photorealRef.current = null
      setAttribution([])
    }
  }, [photorealOn, mapsKey, mapLoaded])

  // Live data: filter to the current fleet toggle, push to the source every poll.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReady.current) return
    const src = map.getSource('global-vehicles') as GeoJSONSource | undefined
    if (!src) return
    const shown = filter === 'all' ? vehicles : vehicles.filter((v) => v.fleet === filter)
    src.setData(toGeoJSON(shown))
  }, [vehicles, filter])

  // Re-fit whenever the fleet toggle changes -- narrowing to one fleet should zoom to
  // where that fleet actually operates, not leave the camera framed for all three.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !styleReady.current || fittedForRef.current === filter) return
    const shown = filter === 'all' ? vehicles : vehicles.filter((v) => v.fleet === filter)
    if (shown.length === 0) return
    fittedForRef.current = filter
    fitToVehicles(map, shown)
  }, [filter, vehicles])

  return (
    <div className="relative h-full w-full">
      <div ref={container} className="h-full w-full" />
      <div className="pointer-events-auto absolute bottom-3 right-3 z-10 flex flex-col items-end gap-1">
        <button
          type="button"
          disabled={!photorealAvailable}
          onClick={() => setPhotorealOn((v) => !v)}
          title={
            photorealAvailable
              ? 'Google Photorealistic 3D Tiles. Billed per session, needs internet.'
              : 'Set GOOGLE_MAPS_API_KEY on the serving service to enable.'
          }
          className={clsx(
            'rounded-full px-3 py-1.5 text-[11px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.12)] backdrop-blur transition-colors disabled:cursor-not-allowed disabled:opacity-40',
            photorealOn ? 'bg-[#D97706] text-white' : 'bg-white/90 text-[#0F172A] hover:bg-white',
          )}
        >
          photoreal 3D
        </button>
        {photorealOn && attribution.length > 0 && (
          <div className="max-w-[220px] truncate rounded-full bg-white/80 px-2 py-0.5 text-[10px] text-[#64748B] backdrop-blur"
               title={attribution.join(' · ')}>
            {attribution.join(' · ')}
          </div>
        )}
      </div>
    </div>
  )
}

// Re-exported so a caller building its own legend never has to hand-copy this map's
// exact colour keys and drift out of sync with it.
export { FLEET_LABEL, STATE_LABEL, MARKER_COLOR as GLOBAL_STATE_COLOR }
